// src/composables/useWeeklyTargetEngine.js
import { computed } from "vue";
import { useProgressStore } from "@/stores/progressStore";
import { useAssessmentStore } from "@/stores/assessmentStore";

function isSameWeek(ts, weekStart) {
  if (typeof weekStart === "string") {
      // Try parsing as ISO date string (e.g. '2026-01-05')
      // This will treat as UTC midnight
      weekStart = Date.parse(weekStart);
    }
    if (typeof weekStart !== "number" || isNaN(weekStart)) {
      return null;
    }
  return ts >= weekStart && ts < weekStart + 7 * 24 * 60 * 60 * 1000;
}

function getWeekStart(ts) {
  const d = new Date(ts);
  const day = d.getDay(); // 0=Sun
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function useWeeklyTargetEngineCore(childId) {
  const progressStore = useProgressStore();

  /* -----------------------------
     RAW INPUTS
  ------------------------------ */

  function getWeeklyTarget(ts = new Date()) {
    const wkStart = getWeekStart(new Date());
    if (!Array.isArray(progressStore.weeklyTargetsCore[childId])) {
      progressStore.weeklyTargetsCore[childId] = [];
    }
    const weekArr = progressStore.weeklyTargetsCore[childId];    
    // Find if weekStart already exists
    let weekObj = weekArr.find(w => w.weekStart === wkStart);
    if (weekObj) {
      return weekObj;
    }
    const newPlan = progressStore.defaultWeekyPlan(wkStart);
    weekArr.push(newPlan);
    return newPlan
  };

  const target = computed(() => getWeeklyTarget());

  const activity = computed(() =>
    progressStore.activityLog[childId] || []
  );

  /* -----------------------------
     FILTER ACTIVITY THIS WEEK
  ------------------------------ */

  const weeklyActivity = computed(() => {
    if (!target.value) return [];
    return activity.value.filter(e =>
      isSameWeek(e.timestamp, target.value.weekStart)
    );
  });

  /* -----------------------------
     COMPLETED COUNTS
  ------------------------------ */

  const completed = computed(() => {
    const counts = {
      lessons: 0,
      revisions: 0,
      tests: 0
    };

    for (const e of weeklyActivity.value) {
      if (e.type === "lesson") counts.lessons++;
      if (e.type === "revision") counts.revisions++;
      if (e.type === "test") counts.tests++;
    }

    return counts;
  });

  /* -----------------------------
     REMAINING COUNTS
  ------------------------------ */

  const remaining = computed(() => {
    if (!target.value) return null;

    return {
      lessons: Math.max(0, target.value.plan.lessons - completed.value.lessons),
      revisions: Math.max(0, target.value.plan.revisions - completed.value.revisions),
      tests: Math.max(0, target.value.plan.tests - completed.value.tests)
    };
  });

  /* -----------------------------
     PACE STATUS
  ------------------------------ */

  const expectedByNow = computed(() => {
    if (!target.value) return null;

    // Ensure weekStart is a timestamp (number). If string, parse it.
    let weekStart = target.value.weekStart;
    if (typeof weekStart === "string") {
      // Try parsing as ISO date string (e.g. '2026-01-05')
      // This will treat as UTC midnight
      weekStart = Date.parse(weekStart);
    }
    if (typeof weekStart !== "number" || isNaN(weekStart)) {
      return null;
    }

    const elapsed =
      (Date.now() - weekStart) /
      (7 * 24 * 60 * 60 * 1000);

    return {
      lessons: Math.round(target.value.plan.lessons * elapsed),
      revisions: Math.round(target.value.plan.revisions * elapsed),
      tests: Math.round(target.value.plan.tests * elapsed)
    };
  });

  const pace = computed(() => {
    if (!expectedByNow.value) return "unknown";

    const behind =
      completed.value.lessons < expectedByNow.value.lessons ||
      completed.value.revisions < expectedByNow.value.revisions ||
      completed.value.tests < expectedByNow.value.tests;

    const ahead =
      completed.value.lessons > expectedByNow.value.lessons &&
      completed.value.revisions > expectedByNow.value.revisions;

    if (ahead) return {status: "ahead", message: "You're ahead this week", color: "text-green-600"};
    if (behind) return {status: "behind", message: "A little more practice will help", color: "text-orange-500"};
    return {status: "on-track", message: "You're right on track", color: "text-blue-600"};
  });

  /* -----------------------------
     PUBLIC API
  ------------------------------ */

  return {
    completed, 
    remaining,
    expectedByNow,
    pace,
    getWeeklyTarget
  };
}
